import React from 'react';
import { motion } from 'framer-motion';
import { QuoteBlock, BoldStatement, ChecklistItem, MessageTemplate, NightCard, EmergencyCard, ProfileCard, ChapterHeading, SectionDivider } from './ui/BookElements';
const FadeSection = ({
  children,
  id,
  className = ''
}: {
  children: React.ReactNode;
  id?: string;
  className?: string;
}) => <motion.section id={id} initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.8
}} className={`py-12 md:py-16 ${className}`} data-id="element-1">
    {children}
  </motion.section>;
export const BookContent = () => {
  return <div className="max-w-3xl mx-auto px-6 md:px-12 pb-32" data-id="element-2">
      {/* Aviso Importante */}
      <FadeSection id="aviso" className="border-b border-navy-800" data-id="element-3">
        <div className="bg-navy-900/50 p-6 md:p-8 rounded-lg border-l-4 border-emergency-600" data-id="element-4">
          <h3 className="text-emergency-600 font-bold uppercase tracking-wider mb-4 text-sm" data-id="element-5">
            Aviso Importante
          </h3>
          <p className="text-cream-200 mb-4 leading-relaxed" data-id="element-6">
            Este material es educativo y de apoyo práctico. No sustituye
            atención médica, psicológica, psiquiátrica ni terapia.
          </p>
          <p className="text-cream-100 font-medium mb-4" data-id="element-7">
            Si estás en riesgo de hacerte daño, o sientes que no estás a salvo,
            busca ayuda inmediata en tu zona (en USA puedes llamar o enviar
            texto al{' '}
            <a href="tel:988" className="text-emergency-600 underline decoration-2 underline-offset-2" data-id="element-8">
              988
            </a>{' '}
            — Suicide & Crisis Lifeline).
          </p>
          <p className="text-cream-200 text-sm" data-id="element-9">
            Si tienes síntomas graves o persistentes (insomnio severo, ataques
            de pánico frecuentes, depresión profunda), consulta a un
            profesional.
          </p>
        </div>
      </FadeSection>

      {/* Intro Page */}
      <FadeSection id="intro" data-id="element-10">
        <div className="text-center space-y-6 py-12" data-id="element-11">
          <h2 className="font-serif text-3xl md:text-4xl text-cream-100" data-id="element-12">
            10 Noches Sin Que La Noche Te Trague
          </h2>
          <div className="w-16 h-px bg-amber-500 mx-auto" data-id="element-13"></div>
          <p className="text-xl text-cream-200 italic font-serif" data-id="element-14">
            Un ritual corto para que el silencio pese menos
          </p>
          <p className="text-amber-500 font-medium" data-id="element-15">
            Protocolo 3R (20–30 minutos × 10 noches)
          </p>
        </div>
      </FadeSection>

      <SectionDivider data-id="element-16" />

      {/* Chapter 1 */}
      <FadeSection id="cap1" data-id="element-17">
        <ChapterHeading number="1" title="Por qué la noche pega más fuerte" id="cap1-heading" data-id="element-18" />

        <p className="text-lg md:text-xl text-cream-200 leading-relaxed mb-6" data-id="element-19">
          La noche no crea tus pensamientos. <br data-id="element-20" />
          <span className="text-cream-100 font-medium" data-id="element-21">
            La noche los amplifica.
          </span>
        </p>

        <p className="text-cream-200 leading-relaxed mb-6" data-id="element-22">
          De día hay ruido: tareas, gente, tráfico, televisión, obligaciones.
          Aunque estés cansado/a, hay estructura. De noche, cuando el mundo baja
          el volumen, la mente ocupa el espacio completo.
        </p>

        <div className="space-y-4 my-8 pl-4 border-l border-navy-800" data-id="element-23">
          <p className="text-cream-200" data-id="element-24">
            ✦ El silencio se siente como evidencia: "si nadie me busca, es
            porque no importo".
          </p>
          <p className="text-cream-200" data-id="element-25">
            ✦ El cansancio se convierte en tristeza: "no era cansancio, era
            vacío".
          </p>
          <p className="text-cream-200" data-id="element-26">
            ✦ Los recuerdos se hacen más vivos: porque ya no hay nada que los
            tape.
          </p>
          <p className="text-cream-200" data-id="element-27">
            ✦ El futuro se vuelve más pesado: porque no hay acción, solo
            película.
          </p>
        </div>

        <p className="text-cream-100 font-medium text-lg mb-6" data-id="element-28">
          No es que seas débil.
        </p>
        <p className="text-cream-200 leading-relaxed mb-8" data-id="element-29">
          Es que la noche sin plan se parece a estar encerrado con un juez que
          conoce tus peores frases.
        </p>

        <QuoteBlock data-id="element-30">No necesitas ser social. Necesitas un ritual.</QuoteBlock>

        <p className="text-cream-200 leading-relaxed" data-id="element-31">
          Un ritual corto no es "motivación". Es un <strong data-id="element-32">puente</strong>: del
          silencio que te traga a una noche que puedes atravesar con dignidad.
        </p>
      </FadeSection>

      <SectionDivider data-id="element-33" />

      {/* Chapter 2 */}
      <FadeSection id="cap2" data-id="element-34">
        <ChapterHeading number="2" title="El error común: intentar 'pensar mejor' sin cambiar el escenario" id="cap2-heading" data-id="element-35" />

        <p className="text-cream-200 leading-relaxed mb-6" data-id="element-36">
          Cuando la noche cae, mucha gente intenta una de estas salidas:
        </p>

        <ul className="list-disc list-inside space-y-2 text-cream-200 mb-8 ml-4 marker:text-amber-500" data-id="element-37">
          <li data-id="element-38">"Me duermo con la TV"</li>
          <li data-id="element-39">"Me pongo a scrollear"</li>
          <li data-id="element-40">"Me tomo algo y ya"</li>
          <li data-id="element-41">"Me digo 'no pienses'"</li>
          <li data-id="element-42">"Intento meditar… y me desespero"</li>
        </ul>

        <p className="text-cream-200 leading-relaxed mb-6" data-id="element-43">
          El problema: todas son <strong data-id="element-44">soluciones sin estructura</strong>. Te
          calman un rato, sí. Pero no te dan una ruta repetible. Y lo que se
          repite cada noche necesita <strong data-id="element-45">ruta</strong>, no consejo.
        </p>

        <BoldStatement data-id="element-46">
          Cuando la mente está pesada, no negocies con pensamientos. Negocia con
          el ambiente y con la acción.
        </BoldStatement>

        <p className="text-cream-200 leading-relaxed" data-id="element-47">
          Por eso este libro empieza por afuera (Re-encender), sigue con las
          manos (Re-ocupar) y termina con un puente humano mínimo (Re-conectar).
        </p>
      </FadeSection>

      <SectionDivider data-id="element-48" />

      {/* Chapter 3 */}
      <FadeSection id="cap3" data-id="element-49">
        <ChapterHeading number="3" title="El Protocolo 3R (visión completa)" id="cap3-heading" data-id="element-50" />

        <p className="text-cream-200 leading-relaxed mb-8" data-id="element-51">
          El Protocolo 3R es una secuencia de 20–30 minutos. No es una "rutina
          perfecta". Es un <strong data-id="element-52">procedimiento</strong> para noches difíciles.
        </p>

        <div className="bg-navy-800/50 p-6 rounded-xl border border-navy-800 space-y-6" data-id="element-53">
          <div className="flex gap-4" data-id="element-54">
            <div className="w-12 h-12 rounded-full bg-navy-900 flex items-center justify-center text-amber-500 font-bold text-xl border border-amber-500/30 shrink-0" data-id="element-55">
              R1
            </div>
            <div data-id="element-56">
              <h4 className="text-cream-100 font-bold text-lg" data-id="element-57">
                Re-encender (5–8 min)
              </h4>
              <p className="text-cream-300" data-id="element-58">
                Cambias el cuarto para que tu sistema nervioso baje 1 nivel.
              </p>
            </div>
          </div>
          <div className="flex gap-4" data-id="element-59">
            <div className="w-12 h-12 rounded-full bg-navy-900 flex items-center justify-center text-amber-500 font-bold text-xl border border-amber-500/30 shrink-0" data-id="element-60">
              R2
            </div>
            <div data-id="element-61">
              <h4 className="text-cream-100 font-bold text-lg" data-id="element-62">
                Re-ocupar (10–15 min)
              </h4>
              <p className="text-cream-300" data-id="element-63">
                Haces una micro-tarea con sentido para que tu mente vuelva a
                "presente".
              </p>
            </div>
          </div>
          <div className="flex gap-4" data-id="element-64">
            <div className="w-12 h-12 rounded-full bg-navy-900 flex items-center justify-center text-amber-500 font-bold text-xl border border-amber-500/30 shrink-0" data-id="element-65">
              R3
            </div>
            <div data-id="element-66">
              <h4 className="text-cream-100 font-bold text-lg" data-id="element-67">
                Re-conectar (3–7 min)
              </h4>
              <p className="text-cream-300" data-id="element-68">
                Haces un contacto seguro (o lo preparas) para no quedarte
                aislado/a con tu película.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 p-4 bg-amber-500/10 rounded-lg border-l-2 border-amber-500" data-id="element-69">
          <p className="text-cream-100 font-medium" data-id="element-70">
            Regla de oro: Si no tienes energía, no improvises: reduce.
          </p>
        </div>

        <div className="mt-8" data-id="element-71">
          <h4 className="text-amber-500 font-serif text-xl mb-4" data-id="element-72">
            Lo que buscamos en 10 noches:
          </h4>
          <ol className="list-decimal list-inside space-y-2 text-cream-200 marker:text-amber-500 marker:font-bold" data-id="element-73">
            <li data-id="element-74">Que el silencio pese menos</li>
            <li data-id="element-75">Que tu cabeza tenga menos película</li>
            <li data-id="element-76">Que tú vuelvas a sentir: "tengo un plan"</li>
          </ol>
        </div>
      </FadeSection>

      <SectionDivider data-id="element-77" />

      {/* Chapter 4 */}
      <FadeSection id="cap4" data-id="element-78">
        <ChapterHeading number="4" title="R1 — Re-encender: baja el volumen del cuarto" id="cap4-heading" data-id="element-79" />

        <p className="text-cream-200 leading-relaxed mb-6" data-id="element-80">
          Re-encender no significa prender todas las luces y "hacer fiesta".
          Significa: crear señales pequeñas de "estoy aquí y estoy a salvo".
        </p>

        <h3 className="text-xl text-cream-100 font-bold mb-6 mt-8" data-id="element-81">
          Checklist R1 (elige 3, no 10)
        </h3>

        <div className="space-y-6" data-id="element-82">
          <div data-id="element-83">
            <h4 className="text-amber-500 font-medium mb-3 uppercase text-sm tracking-wider" data-id="element-84">
              Luz (1 opción)
            </h4>
            <div className="space-y-2" data-id="element-85">
              <ChecklistItem id="r1-light-1" label="Lámpara cálida (amarilla) en lugar de luz blanca fuerte" data-id="element-86" />
              <ChecklistItem id="r1-light-2" label="Luz baja en esquina (no techo)" data-id="element-87" />
              <ChecklistItem id="r1-light-3" label="Si puedes: una vela eléctrica (segura)" data-id="element-88" />
            </div>
          </div>

          <div data-id="element-89">
            <h4 className="text-amber-500 font-medium mb-3 uppercase text-sm tracking-wider" data-id="element-90">
              Sonido (1 opción)
            </h4>
            <div className="space-y-2" data-id="element-91">
              <ChecklistItem id="r1-sound-1" label="Radio bajito (hablada o música suave)" data-id="element-92" />
              <ChecklistItem id="r1-sound-2" label="Sonido constante (ventilador, ruido blanco)" data-id="element-93" />
              <ChecklistItem id="r1-sound-3" label="Una playlist 'sin recuerdos' (importante)" data-id="element-94" />
            </div>
          </div>

          <div data-id="element-95">
            <h4 className="text-amber-500 font-medium mb-3 uppercase text-sm tracking-wider" data-id="element-96">
              Cuerpo/temperatura (1 opción)
            </h4>
            <div className="space-y-2" data-id="element-97">
              <ChecklistItem id="r1-body-1" label="Té caliente o agua tibia" data-id="element-98" />
              <ChecklistItem id="r1-body-2" label="Calcetines / manta en piernas" data-id="element-99" />
              <ChecklistItem id="r1-body-3" label="Ducha corta tibia (si tienes energía)" data-id="element-100" />
            </div>
          </div>

          <div data-id="element-101">
            <h4 className="text-amber-500 font-medium mb-3 uppercase text-sm tracking-wider" data-id="element-102">
              Orden mínimo (1 opción)
            </h4>
            <div className="space-y-2" data-id="element-103">
              <ChecklistItem id="r1-order-1" label="'Reset de 2 minutos': basura fuera + una superficie despejada" data-id="element-104" />
              <ChecklistItem id="r1-order-2" label="Dejar lista una silla/mesa para la micro-tarea" data-id="element-105" />
            </div>
          </div>
        </div>

        <div className="mt-10 bg-navy-800 p-6 rounded-lg" data-id="element-106">
          <h4 className="text-cream-100 font-bold mb-2" data-id="element-107">Por qué funciona</h4>
          <p className="text-cream-200" data-id="element-108">
            Tu mente interpreta el entorno. Si el entorno dice "apagón", la
            mente dice "peligro/abandono". Si el entorno dice "hogar activo", la
            mente baja un grado.
          </p>
        </div>
      </FadeSection>

      <SectionDivider data-id="element-109" />

      {/* Chapter 5 */}
      <FadeSection id="cap5" data-id="element-110">
        <ChapterHeading number="5" title="R2 — Re-ocupar: micro-tarea con sentido" id="cap5-heading" data-id="element-111" />

        <p className="text-cream-200 leading-relaxed mb-6" data-id="element-112">
          Aquí está el corazón del método. La micro-tarea no es para "ser mejor
          persona". Es para{' '}
          <strong data-id="element-113">ocupar tu mente con algo que se pueda terminar</strong>.
        </p>

        <QuoteBlock data-id="element-114">Porque lo que no termina… se queda girando.</QuoteBlock>

        <div className="grid md:grid-cols-3 gap-4 my-8" data-id="element-115">
          <div className="bg-navy-800 p-4 rounded text-center border border-navy-700" data-id="element-116">
            <span className="block text-amber-500 font-bold text-xl mb-1" data-id="element-117">
              10-15
            </span>
            <span className="text-sm text-cream-300" data-id="element-118">Minutos máximo</span>
          </div>
          <div className="bg-navy-800 p-4 rounded text-center border border-navy-700" data-id="element-119">
            <span className="block text-amber-500 font-bold text-xl mb-1" data-id="element-120">
              Inicio/Fin
            </span>
            <span className="text-sm text-cream-300" data-id="element-121">Debe tener cierre</span>
          </div>
          <div className="bg-navy-800 p-4 rounded text-center border border-navy-700" data-id="element-122">
            <span className="block text-amber-500 font-bold text-xl mb-1" data-id="element-123">
              Visible
            </span>
            <span className="text-sm text-cream-300" data-id="element-124">Algo queda hecho</span>
          </div>
        </div>

        <h3 className="text-xl text-cream-100 font-bold mb-6" data-id="element-125">
          Biblioteca de micro-tareas (elige 1 por noche)
        </h3>

        <div className="space-y-8" data-id="element-126">
          <div className="border-l-2 border-green-500/50 pl-4" data-id="element-127">
            <h4 className="text-green-400 font-bold mb-2" data-id="element-128">
              Nivel baja energía
            </h4>
            <ul className="space-y-2 text-cream-200" data-id="element-129">
              <li data-id="element-130">• Doblar 10 prendas (solo 10)</li>
              <li data-id="element-131">• Lavar 5 platos (solo 5)</li>
              <li data-id="element-132">• Preparar ropa para mañana (solo eso)</li>
              <li data-id="element-133">
                • Escribir 6 líneas: "lo que hoy me pesó / lo que sí hice"
              </li>
            </ul>
          </div>

          <div className="border-l-2 border-amber-500/50 pl-4" data-id="element-134">
            <h4 className="text-amber-400 font-bold mb-2" data-id="element-135">
              Nivel energía media
            </h4>
            <ul className="space-y-2 text-cream-200" data-id="element-136">
              <li data-id="element-137">• "Mesa limpia": despejar una mesa completa</li>
              <li data-id="element-138">• Preparar una bolsa para donar (solo una)</li>
              <li data-id="element-139">• Ordenar un cajón (solo uno)</li>
              <li data-id="element-140">• Preparar desayuno simple para mañana</li>
            </ul>
          </div>

          <div className="border-l-2 border-red-500/50 pl-4" data-id="element-141">
            <h4 className="text-red-400 font-bold mb-2" data-id="element-142">
              Nivel energía alta (si te da)
            </h4>
            <ul className="space-y-2 text-cream-200" data-id="element-143">
              <li data-id="element-144">• Caminar 8–12 minutos dentro o afuera</li>
              <li data-id="element-145">• Cocinar algo simple para 2 días</li>
              <li data-id="element-146">• Llamar para agendar una cita pendiente</li>
            </ul>
          </div>
        </div>

        <BoldStatement data-id="element-147">"No soy un adorno en mi propia casa."</BoldStatement>

        <div className="mt-8 p-6 bg-navy-800 rounded-lg text-center" data-id="element-148">
          <p className="text-cream-300 text-sm uppercase tracking-widest mb-2" data-id="element-149">
            Cierre obligatorio
          </p>
          <p className="font-serif italic text-2xl text-cream-100" data-id="element-150">
            "Hecho. Por hoy, basta."
          </p>
        </div>
      </FadeSection>

      <SectionDivider data-id="element-151" />

      {/* Chapter 6 */}
      <FadeSection id="cap6" data-id="element-152">
        <ChapterHeading number="6" title="R3 — Re-conectar: un contacto seguro" id="cap6-heading" data-id="element-153" />

        <p className="text-cream-200 leading-relaxed mb-6" data-id="element-154">
          La noche se pone peligrosa cuando se vuelve "yo contra mi cabeza".
          Re-conectar no es contar tu vida ni pedir rescate. Es crear un{' '}
          <strong data-id="element-155">hilo humano mínimo</strong>.
        </p>

        <div className="mb-8" data-id="element-156">
          <h4 className="text-amber-500 font-bold mb-2" data-id="element-157">
            Define "contacto seguro"
          </h4>
          <p className="text-cream-200" data-id="element-158">
            Una persona que: No te juzga, No te exige explicación, No te deja
            peor.
          </p>
        </div>

        <h3 className="text-xl text-cream-100 font-bold mb-6" data-id="element-159">
          Guiones de 1 mensaje
        </h3>

        <MessageTemplate title="Opción 1 — simple" text="Hola. Solo quería saludar. ¿Cómo estás hoy?" data-id="element-160" />
        <MessageTemplate title="Opción 2 — honesta sin drama" text="Hoy la noche está pesada. No necesito resolver nada, solo un 'hola'." data-id="element-161" />
        <MessageTemplate title="Opción 3 — con dignidad" text="No quiero molestar. Solo quería escuchar una voz un minuto si puedes." data-id="element-162" />
        <MessageTemplate title="Opción 4 — si no quieres hablar" text="¿Me mandas un emoji cuando puedas? Solo para sentir compañía." data-id="element-163" />

        <div className="mt-8 p-4 border border-navy-700 rounded bg-navy-900/30" data-id="element-164">
          <h4 className="text-cream-100 font-bold mb-2" data-id="element-165">
            Si no hay nadie (todavía)
          </h4>
          <ul className="list-disc list-inside text-cream-200 space-y-1" data-id="element-166">
            <li data-id="element-167">Escribir el mensaje y dejarlo listo (en notas)</li>
            <li data-id="element-168">Buscar 1 comunidad local para mañana</li>
            <li data-id="element-169">Guardar el 988 en contactos (prevención)</li>
          </ul>
        </div>
      </FadeSection>

      <SectionDivider data-id="element-170" />

      {/* Chapter 7 */}
      <FadeSection id="cap7" data-id="element-171">
        <ChapterHeading number="7" title="Las 10 noches (calendario paso a paso)" id="cap7-heading" data-id="element-172" />

        <p className="text-cream-200 mb-8" data-id="element-173">
          Esto es lo que hace que el método funcione:{' '}
          <strong data-id="element-174">repetición guiada</strong>. Hazlo 10 noches seguidas si
          puedes.
        </p>

        <div className="space-y-4" data-id="element-175">
          <NightCard number={1} title="Hacer espacio" r1="Luz cálida + agua" r2="Despejar una superficie (mesa o encimera)" r3="Mandar 'hola' a 1 contacto seguro" data-id="element-176" />
          <NightCard number={2} title="Cerrar el día" r1="Sonido suave constante" r2="Escribir 6 líneas (peso / una cosa que sí hice)" r3="Pedir un emoji o 'buenas noches'" data-id="element-177" />
          <NightCard number={3} title="Cuerpo primero" r1="Ducha tibia corta o lavar cara/manos" r2="Preparar ropa de mañana" r3="Mensaje simple: '¿Qué fue lo mejor de tu día?'" data-id="element-178" />
          <NightCard number={4} title="Orden mínimo" r1="Luz + reset de 2 minutos" r2="5 platos o 10 prendas" r3="Enviar un audio de 10 segundos (solo saludo)" data-id="element-179" />
          <NightCard number={5} title="Algo que me cuide" r1="Té/agua + manta" r2="Preparar desayuno simple para mañana" r3="Escribir a alguien: 'Te pensé hoy.'" data-id="element-180" />
          <NightCard number={6} title="Recuperar control" r1="Sonido + luz baja" r2="Ordenar un cajón" r3="Pedir una llamada corta (5 minutos) o un audio" data-id="element-181" />
          <NightCard number={7} title="Caminar la película" r1="Cambiar de cuarto (si puedes)" r2="Caminar 8–12 minutos (adentro sirve)" r3="Mensaje: 'Mañana te cuento algo' (abre hilo)" data-id="element-182" />
          <NightCard number={8} title="Volver a mí" r1="Ambiente cálido" r2="Lista de 10 cosas pequeñas que sí sostienes" r3="Mandar una foto simple (té, mesa, noche) con 'aquí ando'" data-id="element-183" />
          <NightCard number={9} title="Preparar mañana" r1="Luz + agua" r2="Preparar 1 cosa que te facilite la vida mañana" r3="Confirmar un plan mínimo con alguien" data-id="element-184" />
          <NightCard number={10} title="Tu ritual final" r1="Elige tus 2 señales más efectivas" r2="Tu micro-tarea favorita" r3="Tu contacto seguro (o tu plan de conexión)" data-id="element-185" />
        </div>

        <div className="mt-8 text-center" data-id="element-186">
          <p className="text-cream-200 mb-2" data-id="element-187">Al final de la noche 10:</p>
          <p className="font-serif italic text-2xl text-amber-500" data-id="element-188">
            "Cuando la noche venga, yo hago esto."
          </p>
        </div>
      </FadeSection>

      <SectionDivider data-id="element-189" />

      {/* Chapter 8 */}
      <FadeSection id="cap8" data-id="element-190">
        <ChapterHeading number="8" title="Si hoy fue una noche mala: reiniciar sin culpa" id="cap8-heading" data-id="element-191" />

        <QuoteBlock data-id="element-192">Una noche mala no es recaída. Es información.</QuoteBlock>

        <h3 className="text-xl text-cream-100 font-bold mb-4 mt-8" data-id="element-193">
          Protocolo de emergencia (12 minutos)
        </h3>

        <div className="bg-navy-800 p-6 rounded-lg border border-navy-700 space-y-4" data-id="element-194">
          <div className="flex gap-4 items-center" data-id="element-195">
            <div className="text-amber-500 font-bold text-xl w-16" data-id="element-196">2 min</div>
            <div className="text-cream-200" data-id="element-197">Luz cálida + agua</div>
          </div>
          <div className="flex gap-4 items-center" data-id="element-198">
            <div className="text-amber-500 font-bold text-xl w-16" data-id="element-199">7 min</div>
            <div className="text-cream-200" data-id="element-200">
              Micro-tarea ultra simple (5 platos / 10 prendas)
            </div>
          </div>
          <div className="flex gap-4 items-center" data-id="element-201">
            <div className="text-amber-500 font-bold text-xl w-16" data-id="element-202">3 min</div>
            <div className="text-cream-200" data-id="element-203">
              Mensaje emoji a alguien o escribirlo y guardarlo
            </div>
          </div>
        </div>

        <p className="text-center text-cream-300 mt-6 italic" data-id="element-204">
          No pelees por "hacer el libro perfecto".
        </p>
      </FadeSection>

      <SectionDivider data-id="element-205" />

      {/* Chapter 9 */}
      <FadeSection id="cap9" data-id="element-206">
        <ChapterHeading number="9" title="Cuatro perfiles (para que te reconozcas)" id="cap9-heading" data-id="element-207" />

        <div className="grid md:grid-cols-2 gap-6" data-id="element-208">
          <ProfileCard title="1) La persona orgullosa" description="No pide ayuda. Aguanta. Se traga cosas." highlight="R3 le cuesta más, pero le salva más." data-id="element-209" />
          <ProfileCard title="2) La persona cansada" description="Llega drenado/a. Siente que vive en automático." highlight="R2 tiene que ser 'con sentido', no 'más deberes'." data-id="element-210" />
          <ProfileCard title="3) La que quedó sola" description="La casa suena diferente. La cama pesa." highlight="R1 (ambiente) es medicina: cambia el lenguaje de la casa." data-id="element-211" />
          <ProfileCard title="4) El que se siente invisible" description="No es que no tenga gente… es que no se siente visto/a." highlight="R3 con guion le devuelve humanidad sin humillación." data-id="element-212" />
        </div>
      </FadeSection>

      <SectionDivider data-id="element-213" />

      {/* Chapter 10 */}
      <FadeSection id="cap10" data-id="element-214">
        <ChapterHeading number="10" title="Tu versión permanente" id="cap10-heading" data-id="element-215" />

        <p className="text-cream-200 mb-8" data-id="element-216">
          Después de 10 noches, no sigues con todo. Te quedas con tu "mínimo
          viable":
        </p>

        <div className="bg-amber-500/10 p-8 rounded-xl border border-amber-500/30 text-center space-y-4" data-id="element-217">
          <h3 className="font-serif text-2xl text-cream-100 font-bold" data-id="element-218">
            Tu ritual mínimo
          </h3>
          <div className="h-px bg-amber-500/30 w-1/2 mx-auto" data-id="element-219"></div>
          <ul className="text-lg text-cream-100 space-y-2" data-id="element-220">
            <li data-id="element-221">1 señal de luz</li>
            <li data-id="element-222">1 micro-tarea (lista de 5 favoritas)</li>
            <li data-id="element-223">1 contacto seguro (o 1 mensaje preparado)</li>
          </ul>
        </div>
      </FadeSection>

      <SectionDivider data-id="element-224" />

      {/* Annexes */}
      <FadeSection id="anexos" data-id="element-225">
        <h2 className="text-3xl font-serif font-bold text-cream-100 mb-8 text-center" data-id="element-226">
          Anexos
        </h2>

        <EmergencyCard data-id="element-227" />

        <div className="my-12" data-id="element-228">
          <h3 className="text-xl text-amber-500 font-bold mb-4" data-id="element-229">
            Anexo B — Checklist imprimible
          </h3>
          <div className="bg-white text-navy-950 p-6 rounded shadow-lg font-mono text-sm space-y-4" data-id="element-230">
            <div className="flex gap-2" data-id="element-231">
              <div className="w-4 h-4 border border-navy-950" data-id="element-232"></div> R1 (3
              señales máximo): __________
            </div>
            <div className="flex gap-2" data-id="element-233">
              <div className="w-4 h-4 border border-navy-950" data-id="element-234"></div> R2
              (micro-tarea): __________
            </div>
            <div className="flex gap-2" data-id="element-235">
              <div className="w-4 h-4 border border-navy-950" data-id="element-236"></div> R3
              (conexión): __________
            </div>
            <div className="flex gap-2" data-id="element-237">
              <div className="w-4 h-4 border border-navy-950" data-id="element-238"></div> Frase de
              cierre: "Hecho. Por hoy, basta."
            </div>
          </div>
        </div>

        <div className="my-12" data-id="element-239">
          <h3 className="text-xl text-amber-500 font-bold mb-4" data-id="element-240">
            Anexo D — Mini FAQ
          </h3>
          <div className="space-y-6" data-id="element-241">
            <div data-id="element-242">
              <p className="font-bold text-cream-100 mb-1" data-id="element-243">
                ¿Y si me da vergüenza escribirle a alguien?
              </p>
              <p className="text-cream-200" data-id="element-244">
                Usa guion. Corto. Sin explicación. La vergüenza baja cuando hay
                estructura.
              </p>
            </div>
            <div data-id="element-245">
              <p className="font-bold text-cream-100 mb-1" data-id="element-246">
                ¿Y si nadie responde?
              </p>
              <p className="text-cream-200" data-id="element-247">
                No significa que no importas. Significa que es hora de ampliar 1
                contacto seguro (mañana).
              </p>
            </div>
            <div data-id="element-248">
              <p className="font-bold text-cream-100 mb-1" data-id="element-249">
                ¿Cuánto tarda en sentirse diferente?
              </p>
              <p className="text-cream-200" data-id="element-250">
                Muchas personas sienten alivio desde la primera noche por tener
                plan.
              </p>
            </div>
          </div>
        </div>
      </FadeSection>

      {/* Footer / Cierre */}
      <footer className="pt-16 pb-12 text-center border-t border-navy-800 mt-16" data-id="element-251">
        <p className="text-cream-200 mb-6" data-id="element-252">
          No te prometo una vida nueva.
          <br data-id="element-253" />
          Te prometo algo más útil:
        </p>
        <h3 className="font-serif text-3xl md:text-4xl text-amber-500 mb-8" data-id="element-254">
          Que la noche no te encuentre sin plan.
        </h3>
        <p className="text-cream-300 text-sm" data-id="element-255">
          Cuando vuelva el silencio, tú ya sabes qué hacer.
        </p>
      </footer>

      {/* Checkout Section */}
      <section id="checkout" className="scroll-mt-24 pb-32" data-id="element-256">
        <div className="relative bg-gradient-to-b from-navy-800/60 to-navy-900/80 border border-amber-500/20 rounded-2xl p-8 md:p-12 text-center overflow-hidden" data-id="element-257">
          {/* Ambient glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-amber-500/10 rounded-full blur-[80px] pointer-events-none" data-id="element-258"></div>

          <div className="relative z-10 space-y-6" data-id="element-259">
            <p className="text-amber-500 text-sm font-medium uppercase tracking-widest" data-id="element-260">
              Tu ritual empieza esta noche
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-cream-100 font-bold leading-tight" data-id="element-261">
              10 Noches Sin Que
              <br data-id="element-262" />
              La Noche Te Trague
            </h2>
            <p className="text-cream-300 max-w-md mx-auto leading-relaxed" data-id="element-263">
              El protocolo completo — 10 noches guiadas, tarjetas de emergencia,
              guiones de mensaje y tu ritual permanente.
            </p>

            <div className="flex items-center justify-center gap-3 pt-2" data-id="element-264">
              <span className="text-amber-400 font-serif text-4xl font-bold" data-id="element-265">
                $6.36
              </span>
            </div>

            <a href="https://cuerpoencalma.it.com/cart/47453738827963:1" target="_blank" rel="noopener noreferrer" className="mt-4 inline-block px-10 py-4 bg-amber-500 text-navy-950 font-bold text-lg rounded-full shadow-[0_0_30px_rgba(212,165,116,0.35)] hover:bg-amber-400 hover:shadow-[0_0_50px_rgba(212,165,116,0.5)] transition-all duration-300 cursor-pointer" data-id="element-266">
              Comprar Ahora
            </a>

            <p className="text-cream-300/60 text-xs pt-2" data-id="element-267">
              Acceso inmediato · Formato digital · Sin suscripción
            </p>
          </div>
        </div>
      </section>
    </div>;
};